import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff } from 'react-icons/fi';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const categories = ['Food Safety','ISO Certification','Regulatory Compliance','Auditing','Training','Pest Control','Quality Assurance','Other'];
const empty = { title:'', description:'', client:'', category:'', completionDate:'', featured:false, published:true };

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState({ images:[], videos:[], files:[] });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get('/projects/admin/all').then(r => setProjects(r.data)).catch(()=>{});
  useEffect(()=>{ load(); },[]);

  const openForm = (p=null) => {
    setEditing(p);
    setForm(p ? { title:p.title, description:p.description, client:p.client||'', category:p.category||'', completionDate:p.completion_date?p.completion_date.split('T')[0]:'', featured:p.featured, published:p.published } : empty);
    setFiles({ images:[], videos:[], files:[] });
    setShowForm(true); window.scrollTo(0,0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => fd.append(k,v));
      files.images.forEach(f => fd.append('images', f));
      files.videos.forEach(f => fd.append('videos', f));
      files.files.forEach(f => fd.append('files', f));
      if (editing) { await api.put(`/projects/${editing.id}`, fd, { headers:{'Content-Type':'multipart/form-data'} }); toast.success('Project updated!'); }
      else { await api.post('/projects', fd, { headers:{'Content-Type':'multipart/form-data'} }); toast.success('Project created!'); }
      setShowForm(false); setEditing(null); load();
    } catch { toast.error('Failed to save'); }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try { await api.delete(`/projects/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const togglePublish = async (p) => {
    try { await api.put(`/projects/${p.id}`, { published: !p.published }); toast.success(p.published?'Hidden':'Published'); load(); }
    catch { toast.error('Failed'); }
  };

  const inputStyle = { width:'100%', padding:'0.75rem 1rem', border:'1.5px solid #E5E7EB', borderRadius:'10px', fontSize:'0.9rem', fontFamily:'Outfit,sans-serif' };

  return (
    <AdminLayout title="Manage Projects">
      <div style={{ maxWidth:1100 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
          <p style={{ color:'var(--gray-400)', fontSize:'0.875rem' }}>{projects.length} project{projects.length!==1?'s':''}</p>
          <button onClick={()=>openForm()} className="btn btn-navy btn-sm"><FiPlus /> Add Project</button>
        </div>

        {showForm && (
          <motion.div initial={{ opacity:0,y:-16 }} animate={{ opacity:1,y:0 }} style={{ background:'white', borderRadius:'16px', padding:'2rem', marginBottom:'2rem', boxShadow:'var(--shadow)', border:'1px solid #E5E7EB' }}>
            <h3 style={{ fontFamily:'Fraunces,serif', color:'var(--navy)', marginBottom:'1.5rem', fontSize:'1.05rem' }}>{editing?'Edit Project':'Add New Project'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="grid-2">
                <div className="form-group"><label className="form-label">Title *</label><input className="form-input" required value={form.title} onChange={e=>setForm({...form,title:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Client</label><input className="form-input" value={form.client} onChange={e=>setForm({...form,client:e.target.value})} /></div>
              </div>
              <div className="grid-2">
                <div className="form-group"><label className="form-label">Category</label>
                  <select className="form-input" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                    <option value="">-- Select --</option>
                    {categories.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Completion Date</label><input className="form-input" type="date" value={form.completionDate} onChange={e=>setForm({...form,completionDate:e.target.value})} /></div>
              </div>
              <div className="form-group"><label className="form-label">Description *</label><textarea className="form-input" required style={{ minHeight:130 }} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} /></div>
              <div className="grid-2">
                <div className="form-group"><label className="form-label">Images</label><input type="file" multiple accept="image/*" onChange={e=>setFiles({...files,images:[...e.target.files]})} style={{ padding:'0.5rem', border:'1.5px dashed #D1D5DB', borderRadius:'8px', width:'100%', fontSize:'0.83rem' }} /></div>
                <div className="form-group"><label className="form-label">Documents</label><input type="file" multiple accept=".pdf,.doc,.docx" onChange={e=>setFiles({...files,files:[...e.target.files]})} style={{ padding:'0.5rem', border:'1.5px dashed #D1D5DB', borderRadius:'8px', width:'100%', fontSize:'0.83rem' }} /></div>
              </div>
              <div style={{ display:'flex', gap:'2rem', marginBottom:'1.5rem' }}>
                <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', fontSize:'0.875rem' }}><input type="checkbox" checked={form.published} onChange={e=>setForm({...form,published:e.target.checked})} /> Published</label>
                <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', fontSize:'0.875rem' }}><input type="checkbox" checked={form.featured} onChange={e=>setForm({...form,featured:e.target.checked})} /> Featured on homepage</label>
              </div>
              <div style={{ display:'flex', gap:'0.75rem' }}>
                <button type="submit" className="btn btn-navy btn-sm" disabled={submitting}>{submitting?'Saving...':editing?'Update':'Create'}</button>
                <button type="button" className="btn btn-sm" style={{ background:'#F3F4F6', color:'var(--gray-600)' }} onClick={()=>{ setShowForm(false); setEditing(null); }}>Cancel</button>
              </div>
            </form>
          </motion.div>
        )}

        <div style={{ background:'white', borderRadius:'16px', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', border:'1px solid #F3F4F6' }}>
          {projects.length === 0 ? (
            <div style={{ padding:'4rem', textAlign:'center', color:'var(--gray-400)' }}><div style={{ fontSize:'3rem', marginBottom:'1rem' }}>📁</div><p>No projects yet. Click "Add Project" to get started.</p></div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#F9FAFB', borderBottom:'1px solid #F3F4F6' }}>
                {['Project','Category','Client','Status','Actions'].map(h=><th key={h} style={{ padding:'0.875rem 1rem', textAlign:'left', fontSize:'0.73rem', fontWeight:600, color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:'0.08em' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {projects.map((p,i)=>(
                  <tr key={p.id} style={{ borderBottom:'1px solid #F9FAFB', background: i%2===0?'white':'#FAFAFA' }}>
                    <td style={{ padding:'0.875rem 1rem' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                        {p.images?.[0] ? <img src={`${BACKEND}${p.images[0]}`} alt="" style={{ width:44, height:36, objectFit:'cover', borderRadius:'6px' }} /> : <div style={{ width:44, height:36, background:'var(--navy)', borderRadius:'6px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem' }}>🏭</div>}
                        <span style={{ fontWeight:600, color:'var(--navy)', fontSize:'0.875rem' }}>{p.title}</span>
                      </div>
                    </td>
                    <td style={{ padding:'0.875rem 1rem', color:'var(--gray-500)', fontSize:'0.85rem' }}>{p.category||'—'}</td>
                    <td style={{ padding:'0.875rem 1rem', color:'var(--gray-500)', fontSize:'0.85rem' }}>{p.client||'—'}</td>
                    <td style={{ padding:'0.875rem 1rem' }}><span className={`badge badge-${p.published?'approved':'rejected'}`}>{p.published?'Published':'Hidden'}</span></td>
                    <td style={{ padding:'0.875rem 1rem' }}>
                      <div style={{ display:'flex', gap:'0.4rem' }}>
                        <button onClick={()=>togglePublish(p)} style={{ padding:'0.4rem 0.55rem', borderRadius:'7px', background:'#F3F4F6', color:'var(--gray-500)', border:'none', cursor:'pointer', fontSize:'0.85rem' }}>{p.published?<FiEyeOff/>:<FiEye/>}</button>
                        <button onClick={()=>openForm(p)} style={{ padding:'0.4rem 0.55rem', borderRadius:'7px', background:'#DBEAFE', color:'#1E40AF', border:'none', cursor:'pointer', fontSize:'0.85rem' }}><FiEdit2/></button>
                        <button onClick={()=>handleDelete(p.id)} style={{ padding:'0.4rem 0.55rem', borderRadius:'7px', background:'#FEE2E2', color:'#991B1B', border:'none', cursor:'pointer', fontSize:'0.85rem' }}><FiTrash2/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
